import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { adminService } from "../../../services/adminService";
import { pageViewService } from "../../../services/pageViewService";
import ConnectionError from "../../../components/ConnectionError/ConnectionError";
import type {
  AdminChartPoint,
  AdminStatsResponse,
  BookingStatisticsResponse,
  UserRecord,
  PageView,
} from "../../../types";
import { OverviewStats } from "../../../components/dashboard/overview/OverviewStats";
import { GrowthChart } from "../../../components/dashboard/overview/GrowthChart";
import { BookingsChart } from "../../../components/dashboard/overview/BookingsChart";
import { RecentUsersTable } from "../../../components/dashboard/overview/RecentUsersTable";
import { PageViewsCard } from "../../../components/dashboard/overview/PageViewsCard";
import { DashboardGrid } from "../../../components/common/DashboardGrid/DashboardGrid";
import "./Overview.css";

type OverviewRange = "week" | "month" | "year";

function Overview() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<AdminStatsResponse>({
    users: 0,
    bookings: 0,
    messages: 0,
  });
  const [chartData, setChartData] = useState<AdminChartPoint[]>([]);
  const [bookingStats, setBookingStats] = useState<BookingStatisticsResponse>({
    total: 0,
    pending: 0,
    confirmed: 0,
    cancelled: 0,
  });
  const [recentUsers, setRecentUsers] = useState<UserRecord[]>([]);
  const [pageStats, setPageStats] = useState<PageView[]>([]);
  const [range, setRange] = useState<OverviewRange>("month");
  const [loading, setLoading] = useState(true);
  const [loadingChart, setLoadingChart] = useState(false);
  const [error, setError] = useState(false);

  const loadData = () => {
    setLoading(true);
    setError(false);
    Promise.all([
      adminService.getStats().catch(() => null),
      adminService.getChartData(range).catch(() => null),
      adminService.getBookingStatistics().catch(() => null),
      adminService.getUsers(undefined, undefined, 0, 5).catch(() => null),
      pageViewService.getPageStats().catch(() => null),
    ]).then(
      ([
        statsData,
        chartPoints,
        bookingStatsData,
        usersPage,
        pageViewsData,
      ]) => {
        if (
          statsData === null ||
          chartPoints === null ||
          bookingStatsData === null ||
          usersPage === null ||
          pageViewsData === null
        ) {
          setError(true);
        } else {
          setStats(statsData);
          setChartData(chartPoints);
          setBookingStats(bookingStatsData);
          setRecentUsers(usersPage.content);
          setPageStats(pageViewsData);
        }
        setLoading(false);
      },
    );
  };

  // Initial load
  useEffect(() => {
    loadData();
  }, []);

  // Reload chart when range tab changes
  useEffect(() => {
    if (loading) return; // Skip on initial load

    setLoadingChart(true);
    adminService
      .getChartData(range)
      .then((data) => {
        setChartData(data);
        setLoadingChart(false);
      })
      .catch(() => {
        setError(true);
        setLoadingChart(false);
      });
  }, [range]);

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <span className="auth-spinner auth-spinner-brand"></span>
      </div>
    );
  }

  if (error) {
    return <ConnectionError onRetry={loadData} />;
  }

  return (
    <div>
      <h1 className="admin-page-title">{t("admin.overview")}</h1>
      <p className="admin-page-subtitle">{t("admin.overviewDesc")}</p>

      {/* Stats Cards Section (extracted component) */}
      <OverviewStats stats={stats} />

      {/* Charts Section in a beautiful responsive grid layout */}
      <DashboardGrid>
        <GrowthChart
          chartData={chartData}
          range={range}
          onRangeChange={setRange}
          loadingChart={loadingChart}
        />
        <BookingsChart stats={bookingStats} />
      </DashboardGrid>

      {/* Bottom Grid: Recent Users and Page Views */}
      <DashboardGrid>
        <RecentUsersTable users={recentUsers} formatDate={formatDate} />
        <PageViewsCard pageStats={pageStats} />
      </DashboardGrid>
    </div>
  );
}

export default Overview;
